// Shared host classification for environment-conditional code.
// JS mirror of the PHP Environment module (app/Controllers/environment.php);
// both classifiers are pinned to tests/fixtures/host-environments.json, so
// change them together (MTN-613).

const stripPort = (host) => host.split(":")[0];

const isHostOrSubdomain = (host, domain) =>
  host === domain || host.endsWith(`.${domain}`);

const hostnameLabelPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const invalidAuthorityCharacterPattern = /[@/\\?#\s]/;
const hostAuthorityPattern = /^([^:]+)(?::([0-9]{1,5}))?$/;

const parseHostAuthority = (host) => {
  if (
    host === "" ||
    host !== host.trim() ||
    invalidAuthorityCharacterPattern.test(host)
  ) {
    return null;
  }

  const authority = hostAuthorityPattern.exec(host);
  // biome-ignore lint/suspicious/noUnnecessaryConditions: exec() returns null on no match; Biome 2.5.14 types it non-null (biomejs/biome#11278).
  if (authority === null) {
    return null;
  }

  const [, hostname, port] = authority;
  if (
    hostname.length > 253 ||
    (port !== undefined && Number(port) > 65_535) ||
    !hostname.split(".").every((label) => hostnameLabelPattern.test(label))
  ) {
    return null;
  }

  return hostname.toLowerCase();
};

const currentHost = () =>
  typeof window === "undefined" ? "" : window.location.host;

/**
 * Classify a hostname into development | staging | production | unknown.
 *
 * Pure: strips the port and compares case-insensitively. Every
 * classification is an explicit match — local hosts (*.ddev.site, *.test,
 * localhost) are development, exactly beta.moneytalksnews.com is staging,
 * and production requires the www host or the apex. Anything else —
 * including an empty host — is "unknown": neither production (no
 * production side effects) nor development (no development privileges).
 */
export const classifyHost = (host) => {
  if (typeof host !== "string" || host === "") {
    return "unknown";
  }

  const normalized = stripPort(host).toLowerCase();

  if (
    normalized.endsWith(".ddev.site") ||
    normalized.endsWith(".test") ||
    normalized === "localhost"
  ) {
    return "development";
  }

  if (normalized === "beta.moneytalksnews.com") {
    return "staging";
  }

  if (
    normalized === "www.moneytalksnews.com" ||
    normalized === "moneytalksnews.com"
  ) {
    return "production";
  }

  return "unknown";
};

/**
 * Four-way environment classifier for analytics + telemetry.
 */
export const getEnvironment = () => classifyHost(currentHost());

/**
 * True when the current page's host is a local development host
 * (any .ddev.site worktree, .test host, or localhost). Use for opt-in
 * development affordances — debug logging, env=development params on
 * workers, and so on. Beta/staging is NOT local dev.
 */
export const isLocalDevelopment = () =>
  classifyHost(currentHost()) === "development";

/**
 * True when `linkHost` is a host we own — moneytalksnews.com and any of its
 * subdomains, the same for moneytalksnews.test, any `.ddev.site` host
 * (worktree-aware), or bare localhost. Defaults to the current page's host.
 */
export const isInternalMtnHost = (linkHost = currentHost()) => {
  if (typeof linkHost !== "string") {
    return false;
  }

  const normalized = parseHostAuthority(linkHost);
  if (normalized === null) {
    return false;
  }

  if (normalized === "localhost") {
    return true;
  }

  if (
    isHostOrSubdomain(normalized, "moneytalksnews.com") ||
    isHostOrSubdomain(normalized, "moneytalksnews.test")
  ) {
    return true;
  }

  return normalized.endsWith(".ddev.site");
};

// Amazon hosts. Mirrors the allowlists in app/Controllers/amazon.php and
// links.php. Their links open straight to Amazon, never through the click
// proxy (mtn-ur5l6.2), and only these hosts may fill an Amazon box link.
export const AMAZON_HOSTS = new Set([
  "amazon.com",
  "www.amazon.com",
  "smile.amazon.com",
]);

/**
 * True only for HTTP(S) URLs bound for the outbound click proxy. Schemes with
 * no host, such as mailto: and tel:, remain browser-native.
 */
export const shouldProxyExternalUrl = (url) =>
  (url.protocol === "http:" || url.protocol === "https:") &&
  !isInternalMtnHost(url.hostname);
